import React, { useState, useEffect } from 'react';
import styles from "../../style";
import { Link } from 'react-router-dom';

const HeroBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
  }, []);

  const stats = [
    { value: "3+", label: "Cities Deployed" },
    { value: "10x", label: "Faster Processing" },
    { value: "24/7", label: "Monitoring" },
  ];

  return (
    <section id="home" className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-gradient-to-b from-white to-gray-50">

      {/* Animated Background - Full Width */}
      <div className="absolute inset-0">
        <div className="absolute h-[500px] w-[500px] -left-48 -top-48 rounded-full bg-blue-400/20 blur-3xl animate-pulse"></div>
        <div className="absolute h-[500px] w-[500px] -right-48 top-48 rounded-full bg-emerald-400/20 blur-3xl animate-pulse delay-700"></div>
        <div className="absolute h-[500px] w-[500px] left-1/2 -bottom-48 rounded-full bg-purple-400/20 blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 pt-32 pb-20 text-center">
        
        {/* Badge */}
        <div className={`mb-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-50 to-emerald-50 border border-gray-200 px-4 py-2 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4'
        }`}>
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
          </span>
          <span className="text-sm font-medium text-gray-700">AI-Powered Street Intelligence Platform</span>
        </div>

        {/* Main Headline */}
        <h1 className={`mb-8 text-5xl font-bold tracking-tight text-gray-900 sm:text-6xl md:text-7xl lg:text-8xl transition-all duration-1000 delay-100 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <span className="block">Transform Streets Into</span>
          <span className="relative block mt-2">
            <span className="relative z-10 bg-gradient-to-r from-blue-600 via-cyan-600 to-emerald-600 bg-clip-text text-transparent">
              Digital Intelligence
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-emerald-600/20 blur-2xl"></div>
          </span>
        </h1>

        {/* Subheadline */}
        <p className={`mx-auto mb-12 max-w-3xl text-xl text-gray-600 sm:text-2xl transition-all duration-1000 delay-200 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          Turn dashcam footage into comprehensive street infrastructure data. 
          Digitize, analyze, and optimize your entire urban landscape with AI.
        </p>

        {/* CTA Buttons */}
        <div className={`flex flex-wrap justify-center gap-4 mb-16 transition-all duration-1000 delay-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <Link to='/contact'>
            <button className="group relative overflow-hidden rounded-full bg-gradient-to-r from-blue-600 to-emerald-600 px-8 py-4 font-semibold text-white shadow-lg transition-all hover:shadow-2xl hover:scale-105">
              <span className="relative z-10 flex items-center gap-2">
                Start Your Pilot
                <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </span>
              <div className="absolute inset-0 -translate-x-full bg-white/20 transition-transform duration-500 group-hover:translate-x-0"></div>
            </button>
          </Link>
          
          <Link to='/technology'>
            <button className="group rounded-full border-2 border-gray-900 bg-white px-8 py-4 font-semibold text-gray-900 transition-all hover:bg-gray-900 hover:text-white hover:scale-105">
              <span className="flex items-center gap-2">
                Explore Technology
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </span>
            </button>
          </Link>
        </div>

        {/* Stats Section */}
        <div className={`inline-flex flex-wrap justify-center gap-8 rounded-2xl bg-white/80 backdrop-blur-sm border border-gray-200 px-8 py-6 shadow-xl transition-all duration-1000 delay-500 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          {stats.map((stat, index) => (
            <div key={index} className="text-center px-4">
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className={`mt-16 transition-all duration-1000 delay-700 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="flex justify-center">
            <div className="animate-bounce">
              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Optional: Floating Elements for visual interest */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-10 w-72 h-72 bg-gradient-to-br from-blue-400/10 to-transparent rounded-full blur-2xl animate-float"></div>
        <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-gradient-to-br from-emerald-400/10 to-transparent rounded-full blur-2xl animate-float-delayed"></div>
      </div>
    </section>
  );
};

export default HeroBanner;

// Add these animations to your global CSS
const floatAnimation = `
@keyframes float {
  0%, 100% { transform: translateY(0px) }
  50% { transform: translateY(-20px) }
}

@keyframes float-delayed {
  0%, 100% { transform: translateY(0px) }
  50% { transform: translateY(20px) }
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float-delayed 8s ease-in-out infinite;
}
`;